/**
 * Canonical document type identifiers.
 * These are the keys the backend will use to reference specific document types.
 */
export const DOC = {
    US_PASSPORT: 'US_PASSPORT',
    US_PASSPORT_CARD: 'US_PASSPORT_CARD',
    US_DRIVERS_LICENSE: 'US_DRIVERS_LICENSE',
    SSN_CARD: 'SSN_CARD',
    BIRTH_CERTIFICATE: 'BIRTH_CERTIFICATE',
    NATURALIZATION_CERTIFICATE: 'NATURALIZATION_CERTIFICATE',
    PERMANENT_RESIDENT_CARD: 'PERMANENT_RESIDENT_CARD',
    FOREIGN_PASSPORT: 'FOREIGN_PASSPORT',
    I94_I551_ADIT: 'I94_I551_ADIT',
    PASSPORT_I551_ADIT: 'PASSPORT_I551_ADIT',
    EXPIRED_PERMANENT_RESIDENT_CARD: 'EXPIRED_PERMANENT_RESIDENT_CARD',
    I797_EXTENSION: 'I797_EXTENSION',
    EAD_I766: 'EAD_I766',
    EXPIRED_EAD_I766: 'EXPIRED_EAD_I766',
    I797C_RECEIPT: 'I797C_RECEIPT',
    I94: 'I94',
    I797A_APPROVAL: 'I797A_APPROVAL',
}

/**
 * Each flow is a sequential list of steps.
 * Within a step, `accepts` is the single document type that satisfies that step.
 *
 * @typedef {{ stepId: string, purpose: string, label: string, accepts: string }} FlowStep
 * @typedef {{ flowId: string, userType: string, optionId: string, description: string, steps: FlowStep[] }} DocumentFlow
 */

/** @type {DocumentFlow[]} */
export const DOCUMENT_FLOWS = [
    // ── US Citizen ──────────────────────────────────────────────────────────
    {
        flowId: 'us_citizen__passport',
        userType: 'us_citizen',
        optionId: 'passport',
        description: 'US Citizen — Option 1 (One Step): US Passport',
        steps: [
            {
                stepId: 'us_citizen__passport__both_id_and_auth',
                purpose: 'both_id_and_auth',
                label: 'US Passport',
                accepts: DOC.US_PASSPORT,
                veriffIntegration: "us_passport",
            },
        ],
    },
    {
        flowId: 'us_citizen__passport_card',
        userType: 'us_citizen',
        optionId: 'passport_card',
        description: 'US Citizen — Option 1 (One Step): US Passport Card',
        steps: [
            {
                stepId: 'us_citizen__passport_card__both_id_and_auth',
                purpose: 'both_id_and_auth',
                label: 'US Passport Card',
                accepts: DOC.US_PASSPORT_CARD,
                veriffIntegration: "id_card",
            },
        ],
    },
    {
        flowId: 'us_citizen__dl_ssn',
        userType: 'us_citizen',
        optionId: 'dl_ssn_card',
        description: "US Citizen — Option 2 (Two Steps): Driver's License + Social Security Card",
        steps: [
            {
                stepId: 'us_citizen__dl_ssn__id',
                purpose: 'id',
                label: "US State-issued Driver's License / ID",
                accepts: DOC.US_DRIVERS_LICENSE,
                veriffIntegration: "dl_or_id_card",
            },
            {
                stepId: 'us_citizen__dl_ssn__employment_auth',
                purpose: 'employment_auth',
                label: 'Unrestricted "Social Security Card"',
                accepts: DOC.SSN_CARD,
            },
        ],
    },
    {
        flowId: 'us_citizen__dl_birth_cert',
        userType: 'us_citizen',
        optionId: 'dl_birth_cert',
        description: "US Citizen — Option 2 (Two Steps): Driver's License + Birth Certificate",
        steps: [
            {
                stepId: 'us_citizen__dl_birth_cert__id',
                purpose: 'id',
                label: "US State-issued Driver's License / ID",
                accepts: DOC.US_DRIVERS_LICENSE,
                veriffIntegration: "dl_or_id_card",
            },
            {
                stepId: 'us_citizen__dl_birth_cert__employment_auth',
                purpose: 'employment_auth',
                label: 'Original or Certified Birth Certificate',
                accepts: DOC.BIRTH_CERTIFICATE,
            },
        ],
    },
    {
        flowId: 'us_citizen__dl_naturalization',
        userType: 'us_citizen',
        optionId: 'dl_naturalization',
        description: "US Citizen — Option 2 (Two Steps): Driver's License + Certificate of Naturalization",
        steps: [
            {
                stepId: 'us_citizen__dl_naturalization__id',
                purpose: 'id',
                label: "US State-issued Driver's License / ID",
                accepts: DOC.US_DRIVERS_LICENSE,
                veriffIntegration: "dl_or_id_card",
            },
            {
                stepId: 'us_citizen__dl_naturalization__employment_auth',
                purpose: 'employment_auth',
                label: 'Certificate of Naturalization (Form N-550 or N-570)',
                accepts: DOC.NATURALIZATION_CERTIFICATE,
            },
        ],
    },

    // ── Lawful Permanent Resident ────────────────────────────────────────────
    {
        flowId: 'lpr__i551',
        userType: 'lawful_permanent_resident',
        optionId: 'i551',
        description: 'Lawful Permanent Resident — Option 1: Permanent Resident Card (Form I-551)',
        steps: [
            {
                stepId: 'lpr__i551__both_id_and_auth',
                purpose: 'both_id_and_auth',
                label: 'Permanent Resident Card (Form I-551)',
                accepts: DOC.PERMANENT_RESIDENT_CARD,
                veriffIntegration: "residence_permit",
            },
        ],
    },
    {
        flowId: 'lpr__foreign_passport_i94_adit',
        userType: 'lawful_permanent_resident',
        optionId: 'fp_i94_adit',
        description: 'Lawful Permanent Resident — Option 2: Foreign Passport with I-551 ADIT Stamp (Arrival Notice / Form I-94)',
        steps: [
            {
                stepId: 'lpr__foreign_passport_i94_adit__id',
                purpose: 'id',
                label: 'Foreign Passport',
                accepts: DOC.FOREIGN_PASSPORT,
                veriffIntegration: "passport",
            },
            {
                stepId: 'lpr__foreign_passport_i94_adit__employment_auth',
                purpose: 'employment_auth',
                label: 'Arrival Notice (Form I-94) containing an active I-551 ADIT Stamp',
                accepts: DOC.I94_I551_ADIT,
            },
            {
                stepId: 'lpr__foreign_passport_i94_adit__us_photo_id',
                purpose: 'us_photo_id',
                label: "US State-issued Driver's License / ID",
                accepts: DOC.US_DRIVERS_LICENSE,
                veriffIntegration: "dl_or_id_card",
                optional: true,
                skipLabel: 'Do not have US ID',
            },
        ],
    },
    {
        flowId: 'lpr__foreign_passport_passport_adit',
        userType: 'lawful_permanent_resident',
        optionId: 'fp_passport_adit',
        description: 'Lawful Permanent Resident — Option 2: Foreign Passport with I-551 ADIT Stamp (Passport Page)',
        steps: [
            {
                stepId: 'lpr__foreign_passport_passport_adit__id',
                purpose: 'id',
                label: 'Foreign Passport',
                accepts: DOC.FOREIGN_PASSPORT,
                veriffIntegration: "passport",
            },
            {
                stepId: 'lpr__foreign_passport_passport_adit__employment_auth',
                purpose: 'employment_auth',
                label: 'Passport Page containing a physical, active I-551 ADIT Stamp',
                accepts: DOC.PASSPORT_I551_ADIT,
            },
            {
                stepId: 'lpr__foreign_passport_passport_adit__us_photo_id',
                purpose: 'us_photo_id',
                label: "US State-issued Driver's License / ID",
                accepts: DOC.US_DRIVERS_LICENSE,
                veriffIntegration: "dl_or_id_card",
                optional: true,
                skipLabel: 'Do not have US ID',
            },
        ],
    },
    {
        flowId: 'lpr__expired_i551_i797__fp',
        userType: 'lawful_permanent_resident',
        optionId: 'i551_expired_i797__fp',
        description: 'Lawful Permanent Resident — Option 3: Expired PRC with I-797 Extension (optional US ID)',
        steps: [
            {
                stepId: 'lpr__expired_i551_i797__fp__employment_auth_primary',
                purpose: 'employment_auth_primary',
                label: 'Expired Permanent Resident Card (Form I-551)',
                accepts: DOC.EXPIRED_PERMANENT_RESIDENT_CARD,
                veriffIntegration: "id_card",
            },
            {
                stepId: 'lpr__expired_i551_i797__fp__employment_auth_extension',
                purpose: 'employment_auth_extension',
                label: 'Official I-797 Extension Notice',
                accepts: DOC.I797_EXTENSION,
            },
            {
                stepId: 'lpr__expired_i551_i797__fp__us_photo_id',
                purpose: 'us_photo_id',
                label: "US State-issued Driver's License / ID",
                accepts: DOC.US_DRIVERS_LICENSE,
                veriffIntegration: "dl_or_id_card",
                optional: true,
                skipLabel: 'Do not have US ID',
            },
        ],
    },

    // ── EAD Holder ───────────────────────────────────────────────────────────
    {
        flowId: 'ead__i766',
        userType: 'ead_holder',
        optionId: 'ead_i766',
        description: 'EAD Holder — Option 1: Employment Authorization Document (Form I-766)',
        steps: [
            {
                stepId: 'ead__i766__both_id_and_auth',
                purpose: 'both_id_and_auth',
                label: 'Employment Authorization Document (Form I-766)',
                accepts: DOC.EAD_I766,
                veriffIntegration: "id_card",
            },
        ],
    },
    {
        flowId: 'ead__expired_i766_i797c__fp',
        userType: 'ead_holder',
        optionId: 'ead_expired_i797c__fp',
        description: 'EAD Holder — Option 2: Expired EAD with I-797C Extension (optional US ID)',
        steps: [
            {
                stepId: 'ead__expired_i766_i797c__fp__employment_auth_primary',
                purpose: 'employment_auth_primary',
                label: 'Expired EAD Card (Form I-766)',
                accepts: DOC.EXPIRED_EAD_I766,
                veriffIntegration: "id_card",
            },
            {
                stepId: 'ead__expired_i766_i797c__fp__employment_auth_extension',
                purpose: 'employment_auth_extension',
                label: 'I-797C Receipt Notice',
                accepts: DOC.I797C_RECEIPT,
            },
            {
                stepId: 'ead__expired_i766_i797c__fp__us_photo_id',
                purpose: 'us_photo_id',
                label: "US State-issued Driver's License / ID",
                accepts: DOC.US_DRIVERS_LICENSE,
                veriffIntegration: "dl_or_id_card",
                optional: true,
                skipLabel: 'Do not have US ID',
            },
        ],
    },

    // ── Non-Immigrant Visa Holder ────────────────────────────────────────────
    {
        flowId: 'visa__i94',
        userType: 'non_immigrant_visa',
        optionId: 'foreign_passport_i94',
        description: 'Non-Immigrant Visa — Issued abroad or traveled since last US-based extension',
        steps: [
            {
                stepId: 'visa__i94__id',
                purpose: 'id',
                label: 'Foreign Passport',
                accepts: DOC.FOREIGN_PASSPORT,
                veriffIntegration: "passport",
            },
            {
                stepId: 'visa__i94__employment_auth',
                purpose: 'employment_auth',
                label: 'Latest non-expired electronic I-94',
                accepts: DOC.I94,
            },
            {
                stepId: 'visa__i94__us_photo_id',
                purpose: 'us_photo_id',
                label: "US State-issued Driver's License / ID",
                accepts: DOC.US_DRIVERS_LICENSE,
                veriffIntegration: "dl_or_id_card",
                optional: true,
                skipLabel: 'Do not have US ID',
            },
        ],
    },
    {
        flowId: 'visa__i797a',
        userType: 'non_immigrant_visa',
        optionId: 'foreign_passport_i797a',
        description: 'Non-Immigrant Visa — Issued in US and not traveled abroad since issuance',
        steps: [
            {
                stepId: 'visa__i797a__id',
                purpose: 'id',
                label: 'Foreign Passport',
                accepts: DOC.FOREIGN_PASSPORT,
                veriffIntegration: "passport",
            },
            {
                stepId: 'visa__i797a__employment_auth',
                purpose: 'employment_auth',
                label: 'I-797A (Approval Notice) with attached non-expired I-94 section',
                accepts: DOC.I797A_APPROVAL,
            },
            {
                stepId: 'visa__i797a__us_photo_id',
                purpose: 'us_photo_id',
                label: "US State-issued Driver's License / ID",
                accepts: DOC.US_DRIVERS_LICENSE,
                veriffIntegration: "dl_or_id_card",
                optional: true,
                skipLabel: 'Do not have US ID',
            },
        ],
    },
]

/**
 * Given the documents provided so far (and optionally the flow the user selected),
 * determines whether the user is on track, what to expect next, and — if they've
 * drifted — which alternative flows their documents are consistent with.
 *
 * Documents are matched sequentially against each flow's steps.
 * Each step's `accepts` value is the single document type that satisfies that step.
 *
 * @param {string[]} providedDocs   - Ordered array of DOC.* values provided so far.
 * @param {string|null} selectedFlowId - The flowId the user chose in Step 1, or null
 *                                       when no flow has been selected yet.
 *
 * @typedef {{ purpose: string, label: string, accepts: string }} FlowStep
 * @typedef {{
 *   flowId: string,
 *   userType: string,
 *   description: string,
 *   matchedSteps: number,
 *   remainingSteps: FlowStep[],
 *   nextExpectedSteps: FlowStep[],
 * }} MatchedFlow
 *
 * @returns {{
 *   status: 'in_progress' | 'complete' | 'unknown_flow' | 'off_selected_flow',
 *   selectedFlow: MatchedFlow | null,
 *   alternativeFlows: MatchedFlow[],
 *   matchedFlows: MatchedFlow[],
 *   nextExpectedSteps: FlowStep[],
 * }}
 *
 * - `selectedFlow`      — the matched entry for the user's chosen flow, or null when
 *                         the provided docs are inconsistent with that flow.
 * - `alternativeFlows`  — every other flow that IS consistent with the provided docs;
 *                         populated whether or not a selectedFlowId was given.
 * - `matchedFlows`      — full list of all flows consistent with the provided docs.
 * - `nextExpectedSteps` — deduplicated union of the immediate next step across all
 *                         in-progress matched flows (useful when no flow is selected).
 *                         Each entry in `matchedFlows` also carries its own
 *                         `nextExpectedSteps` for per-flow guidance.
 * - status `'off_selected_flow'` — the selected flow was provided but the docs no
 *                         longer match it; `alternativeFlows` lists what they do match.
 */
export function checkDocumentFlow(providedDocs = [], selectedFlowId = null) {
    const matchedFlows = []

    for (const flow of DOCUMENT_FLOWS) {
        let stepCursor = 0
        let isMatch = true

        for (const doc of providedDocs) {
            if (stepCursor >= flow.steps.length) {
                isMatch = false
                break
            }
            if (flow.steps[stepCursor].accepts === doc) {
                stepCursor++
            } else {
                isMatch = false
                break
            }
        }

        if (isMatch) {
            matchedFlows.push({
                flowId: flow.flowId,
                userType: flow.userType,
                description: flow.description,
                matchedSteps: stepCursor,
                remainingSteps: flow.steps.slice(stepCursor),
                nextExpectedSteps: flow.steps.slice(stepCursor, stepCursor + 1),
            })
        }
    }

    if (matchedFlows.length === 0) {
        return {
            status: 'unknown_flow',
            selectedFlow: null,
            alternativeFlows: [],
            matchedFlows: [],
            nextExpectedSteps: [],
        }
    }

    const selectedFlow = selectedFlowId
        ? (matchedFlows.find(m => m.flowId === selectedFlowId) ?? null)
        : null

    const offSelectedFlow = selectedFlowId !== null && selectedFlow === null

    const isComplete = offSelectedFlow
        ? false
        : selectedFlowId
            ? selectedFlow.remainingSteps.length === 0
            : matchedFlows.some(m => m.remainingSteps.length === 0)

    const status = offSelectedFlow ? 'off_selected_flow'
        : isComplete ? 'complete'
            : 'in_progress'

    // All flows that match the docs but are not the selected flow.
    const alternativeFlows = matchedFlows.filter(m => m.flowId !== selectedFlowId)

    // Deduplicated union of the immediate next step across all in-progress matched flows.
    const nextExpectedSteps = []
    const seen = new Set()
    for (const match of matchedFlows) {
        if (match.remainingSteps.length === 0) continue
        const nextStep = match.remainingSteps[0]
        const key = nextStep.accepts
        if (!seen.has(key)) {
            seen.add(key)
            nextExpectedSteps.push(nextStep)
        }
    }

    return {
        status,
        selectedFlow,
        alternativeFlows,
        matchedFlows,
        nextExpectedSteps,
    }
}
